import {ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, inject, Input, OnChanges, OnDestroy} from '@angular/core';
import {AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator} from '@angular/forms';
import {Subject, takeUntil} from 'rxjs';
import {MaterialModule} from '../../../../material.module';
import {HubspotProperty} from '../../../../models/hubspot.model';
import {allCompaniesPolicy, CompanySyncCondition, CompanySyncEligibility, CompanySyncEligibilityPreview,
  CompanySyncPropertyType, CompanySyncRuleOperator} from '../../../../models/company-sync-eligibility.model';
import {HubspotService} from '../../../../services/enrich/hubspot.service';

@Component({
  selector: 'app-company-sync-rules', standalone: true, imports: [MaterialModule],
  templateUrl: './company-sync-rules.component.html', styleUrl: './company-sync-rules.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CompanySyncRulesComponent), multi: true},
    {provide: NG_VALIDATORS, useExisting: forwardRef(() => CompanySyncRulesComponent), multi: true},
  ],
})
export class CompanySyncRulesComponent implements ControlValueAccessor, Validator, OnChanges, OnDestroy {
  @Input() properties: HubspotProperty[] = [];
  @Input() clientId: string | undefined;
  private readonly service = inject(HubspotService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyed$ = new Subject<void>();
  private generation = 0;
  private onChange: (value: CompanySyncEligibility) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private onValidationChange: () => void = () => undefined;
  policy = allCompaniesPolicy();
  disabled = false;
  preview: CompanySyncEligibilityPreview | null = null;
  previewBusy = false;
  previewError: string | null = null;

  get availableProperties(): HubspotProperty[] {
    return this.properties.filter(property => ['string', 'enumeration', 'bool', 'number'].includes(property.type));
  }
  get missingTracking(): HubspotProperty[] {
    const names = new Set(this.policy.groups.flatMap(group => group.conditions.map(condition => condition.propertyName)));
    return this.availableProperties.filter(property => names.has(property.name) && property.automaticSyncWebhookSupported !== true);
  }
  get rulesValid(): boolean {
    if (this.policy.version !== 1 || !['all', 'matching'].includes(this.policy.mode)) return false;
    if (this.policy.mode === 'all') return this.policy.groups.length === 0;
    return this.policy.groups.length > 0 && this.policy.groups.length <= 5 && this.policy.groups.every(group =>
      group.conditions.length > 0 && group.conditions.length <= 10 && group.conditions.every(condition => {
        const property = this.properties.find(candidate => candidate.name === condition.propertyName);
        if (!property || property.type !== condition.propertyType || !this.operators(condition).some(option => option.value === condition.operator)) return false;
        if (['known', 'unknown'].includes(condition.operator)) return condition.values.length === 0;
        if (!condition.values.length || condition.values.length > 20 || condition.values.some(value => !value.trim() || value.length > 256)) return false;
        if (!['in', 'not_in'].includes(condition.operator) && condition.values.length !== 1) return false;
        if (condition.propertyType === 'number' && condition.values.some(value => !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(value) || !Number.isFinite(Number(value)))) return false;
        if (condition.propertyType === 'bool' && condition.values.some(value => !['true', 'false'].includes(value))) return false;
        return condition.propertyType !== 'enumeration' || condition.values.every(value => property.options?.some(option => option.value === value));
      }));
  }
  ngOnChanges(): void {this.resetPreview(); this.onValidationChange();}
  ngOnDestroy(): void {this.generation++; this.destroyed$.next(); this.destroyed$.complete();}
  writeValue(value: CompanySyncEligibility | undefined): void {
    this.policy = value === undefined ? allCompaniesPolicy() : value?.version === 1 && Array.isArray(value.groups) && value.groups.every(group =>
      group && Array.isArray(group.conditions) && group.conditions.every(condition => condition &&
        typeof condition.propertyName === 'string' && typeof condition.propertyType === 'string' &&
        typeof condition.operator === 'string' && Array.isArray(condition.values) && condition.values.every(item => typeof item === 'string')))
      ? JSON.parse(JSON.stringify(value)) : {version: 1, mode: 'matching', groups: []};
    this.resetPreview(); this.cdr.markForCheck();
  }
  registerOnChange(callback: (value: CompanySyncEligibility) => void): void {this.onChange = callback;}
  registerOnTouched(callback: () => void): void {this.onTouched = callback;}
  registerOnValidatorChange(callback: () => void): void {this.onValidationChange = callback;}
  setDisabledState(disabled: boolean): void {this.disabled = disabled; this.cdr.markForCheck();}
  validate(_control: AbstractControl): ValidationErrors | null {
    if (!this.rulesValid) return {companySyncRulesInvalid: true};
    return this.policy.mode === 'matching' && this.missingTracking.length ? {companySyncTrackingMissing: true} : null;
  }
  setMode(mode: 'all' | 'matching'): void {
    this.policy = mode === 'all' ? allCompaniesPolicy() : {version: 1, mode, groups: [{conditions: [this.emptyCondition()]}]};
    this.changed();
  }
  addGroup(): void {if (this.policy.groups.length < 5) {this.policy.groups.push({conditions: [this.emptyCondition()]}); this.changed();}}
  removeGroup(index: number): void {this.policy.groups.splice(index, 1); this.changed();}
  addCondition(groupIndex: number): void {if (this.policy.groups[groupIndex].conditions.length < 10) {this.policy.groups[groupIndex].conditions.push(this.emptyCondition()); this.changed();}}
  removeCondition(groupIndex: number, index: number): void {this.policy.groups[groupIndex].conditions.splice(index, 1); this.changed();}
  setProperty(condition: CompanySyncCondition, name: string): void {
    const property = this.properties.find(candidate => candidate.name === name);
    condition.propertyName = name;
    condition.propertyType = (property?.type ?? 'string') as CompanySyncPropertyType;
    condition.operator = 'known'; condition.values = []; this.changed();
  }
  setOperator(condition: CompanySyncCondition, operator: CompanySyncRuleOperator): void {
    condition.operator = operator; condition.values = []; this.changed();
  }
  setValues(condition: CompanySyncCondition, values: string | string[]): void {
    condition.values = Array.isArray(values) ? [...values] : values.split('\n').map(value => value.trim()).filter(Boolean); this.changed();
  }
  inputValue(condition: CompanySyncCondition, event: Event): void {this.setValues(condition, (event.target as HTMLInputElement).value);}
  options(condition: CompanySyncCondition): Array<{value: string; label: string}> {
    if (condition.propertyType === 'bool') return [{value: 'true', label: 'Yes'}, {value: 'false', label: 'No'}];
    return this.properties.find(property => property.name === condition.propertyName)?.options ?? [];
  }
  operators(condition: CompanySyncCondition): Array<{value: CompanySyncRuleOperator; label: string}> {
    const base: Array<{value: CompanySyncRuleOperator; label: string}> = [
      {value: 'known', label: 'has a value'}, {value: 'unknown', label: 'is empty'},
      {value: 'equals', label: 'is equal to'}, {value: 'not_equals', label: 'is not equal to'},
      {value: 'in', label: 'is any of'}, {value: 'not_in', label: 'is none of'},
    ];
    if (condition.propertyType === 'string') base.push({value: 'contains', label: 'contains'}, {value: 'starts_with', label: 'starts with'}, {value: 'ends_with', label: 'ends with'});
    if (condition.propertyType === 'number') base.push({value: 'gt', label: 'is greater than'}, {value: 'gte', label: 'is at least'}, {value: 'lt', label: 'is less than'}, {value: 'lte', label: 'is at most'});
    return base;
  }
  runPreview(next = false): void {
    if (!this.clientId || !this.rulesValid || this.previewBusy) return;
    const client = this.clientId, policy = JSON.stringify(this.policy), generation = ++this.generation;
    const cursor = next ? this.preview?.nextCursor ?? undefined : undefined;
    this.previewBusy = true; this.previewError = null;
    this.service.previewCompanySyncEligibility(JSON.parse(policy), client, cursor).pipe(takeUntil(this.destroyed$)).subscribe({
      next: response => {
        if (generation !== this.generation || client !== this.clientId || policy !== JSON.stringify(this.policy)) return;
        this.preview = response; this.previewBusy = false; this.cdr.markForCheck();
      },
      error: () => {
        if (generation !== this.generation || client !== this.clientId || policy !== JSON.stringify(this.policy)) return;
        this.preview = null; this.previewBusy = false; this.previewError = 'Preview could not be loaded. Check the company connection and try again.'; this.cdr.markForCheck();
      },
    });
  }
  private emptyCondition(): CompanySyncCondition {return {propertyName: '', propertyType: 'string', operator: 'known', values: []};}
  private changed(): void {this.resetPreview(); this.onChange(JSON.parse(JSON.stringify(this.policy))); this.onTouched(); this.onValidationChange();}
  private resetPreview(): void {this.generation++; this.preview = null; this.previewBusy = false; this.previewError = null;}
}
